import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import {
  canResolveFailure,
  canRetryFailure,
  errorCodeLabel,
  failureStageLabel,
  failureStatusLabel,
  resolutionLabels,
  resolutionValues,
  validateFailureNote,
  type ImportFailureDetail,
  type Resolution,
} from "../entities/mailboxes";
import { formatDate, useSubmissionKey } from "./counterpartiesSupport";
import { MailboxNotice } from "./MailboxNotice";
import { mailboxKeys } from "./mailboxesSupport";
import type { ShellServices } from "./services";
import { Select } from "./Select";

type Service = NonNullable<ShellServices["mailboxes"]>;

export function FailureStatusBadge({ status }: { status: string }) {
  const tone =
    status === "resolved"
      ? "ready"
      : status === "retrying"
        ? "pending"
        : "rejected";
  return (
    <span className={`status-badge status-badge--${tone}`}>
      {failureStatusLabel(status)}
    </span>
  );
}

function contextValue(value: unknown): string {
  return typeof value === "string" ? value : JSON.stringify(value);
}

function ResolveForm({
  pending,
  error,
  onSubmit,
  onCancel,
  onChange,
}: {
  pending: boolean;
  error: unknown;
  onSubmit: (input: { resolution: Resolution; note: string }) => void;
  onCancel: () => void;
  onChange: () => void;
}) {
  const [resolution, setResolution] = useState<Resolution>("manual_fix");
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const problem = validateFailureNote(note);

  function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (problem !== null) return;
    onSubmit({ resolution, note: note.trim() });
  }

  return (
    <form className="cp-form mb-resolve" onSubmit={submit} noValidate>
      <h3 className="mb-resolve__title">Отметить ошибку разобранной</h3>
      <p className="muted">
        Ошибка перейдёт в состояние «Разобрана» и исчезнет из открытых. Письмо
        при этом не загружается заново — для этого используйте «Повторить».
      </p>
      {error === null || error === undefined ? null : (
        <MailboxNotice error={error} fallback="Не удалось отметить ошибку." />
      )}
      <div className="field cp-field">
        <label htmlFor="mb-resolution">
          <span>Как разобрана</span>
        </label>
        <Select
          id="mb-resolution"
          value={resolution}
          onChange={(event) => {
            onChange();
            setResolution(event.target.value as Resolution);
          }}
        >
          {resolutionValues.map((value) => (
            <option key={value} value={value}>
              {resolutionLabels[value]}
            </option>
          ))}
        </Select>
      </div>
      <div className="field cp-field">
        <label htmlFor="mb-resolution-note">
          <span>Заметка</span>
        </label>
        <textarea
          id="mb-resolution-note"
          rows={3}
          maxLength={10000}
          value={note}
          aria-invalid={submitted && problem !== null ? true : undefined}
          aria-describedby={
            submitted && problem !== null
              ? "mb-resolution-note-error"
              : undefined
          }
          onChange={(event) => {
            onChange();
            setNote(event.target.value);
          }}
        />
        {submitted && problem !== null ? (
          <small
            id="mb-resolution-note-error"
            className="field-error"
            role="alert"
          >
            {problem}
          </small>
        ) : null}
      </div>
      <div className="dw-form-actions">
        <button className="button button--primary" disabled={pending}>
          {pending ? "Сохраняем…" : "Отметить разобранной"}
        </button>
        <button
          type="button"
          className="button button--text"
          disabled={pending}
          onClick={onCancel}
        >
          Отмена
        </button>
      </div>
    </form>
  );
}

export function ImportFailureCard({
  service,
  id,
  mailboxNames,
  onBack,
}: {
  service: Service;
  id: string;
  /** Названия ящиков по идентификатору; пусто, если список ящиков недоступен. */
  mailboxNames: ReadonlyMap<string, string>;
  onBack: () => void;
}) {
  const client = useQueryClient();
  const retryKey = useSubmissionKey();
  const resolveKey = useSubmissionKey();
  const [resolving, setResolving] = useState(false);

  const failure = useQuery({
    queryKey: mailboxKeys.failure(id),
    queryFn: () => service.failure(id),
    retry: false,
  });

  async function refresh(updated: ImportFailureDetail) {
    client.setQueryData(mailboxKeys.failure(id), updated);
    await client.invalidateQueries({
      queryKey: [...mailboxKeys.failures, "list"],
    });
  }

  const retry = useMutation({
    // Ключ держится, пока запрос не удался: повтор после сбоя сети не поставит
    // письмо в очередь дважды. После успеха ключ сбрасывается.
    mutationFn: () => service.retryFailure(id, retryKey.current()),
    onSuccess: async (updated) => {
      retryKey.reset();
      await refresh(updated);
    },
    onError: () =>
      void client.invalidateQueries({ queryKey: mailboxKeys.failure(id) }),
  });
  const resolve = useMutation({
    mutationFn: (input: { resolution: Resolution; note: string }) =>
      service.resolveFailure(id, input, resolveKey.current()),
    onSuccess: async (updated) => {
      resolveKey.reset();
      setResolving(false);
      await refresh(updated);
    },
  });

  const back = (
    <button
      type="button"
      className="button button--text cp-back"
      onClick={onBack}
    >
      ← К списку
    </button>
  );

  if (failure.isLoading) return <p aria-busy="true">Загружаем ошибку…</p>;
  if (failure.isError || failure.data === undefined) {
    return (
      <div className="cp-card">
        {back}
        <MailboxNotice
          error={failure.error}
          fallback="Не удалось загрузить ошибку."
          onRetry={() => void failure.refetch()}
          retrying={failure.isFetching}
        />
      </div>
    );
  }

  const data = failure.data;
  const canRetry = canRetryFailure(data);
  const canResolve = canResolveFailure(data);
  const context = Object.entries(data.safe_context);

  return (
    <article className="cp-card mb-card" aria-labelledby="mb-failure-title">
      {back}
      <div className="cp-card__head">
        <div>
          <h2 id="mb-failure-title">{errorCodeLabel(data.error_code)}</h2>
          <p className="muted">{data.safe_message}</p>
        </div>
        <FailureStatusBadge status={data.status} />
      </div>

      <dl className="cp-requisites" aria-label="Сведения об ошибке">
        <div>
          <dt>Ящик</dt>
          <dd>
            {data.mailbox_id === null
              ? "—"
              : (mailboxNames.get(data.mailbox_id) ??
                data.mailbox_id.slice(0, 8))}
          </dd>
        </div>
        <div>
          <dt>Этап</dt>
          <dd>{failureStageLabel(data.stage)}</dd>
        </div>
        <div>
          <dt>Код ошибки</dt>
          <dd>
            <code>{data.error_code}</code>
          </dd>
        </div>
        <div>
          <dt>Попыток</dt>
          <dd>{data.attempts}</dd>
        </div>
        <div>
          <dt>Возникла</dt>
          <dd>{formatDate(data.created_at)}</dd>
        </div>
        <div>
          <dt>Обновлена</dt>
          <dd>{formatDate(data.updated_at)}</dd>
        </div>
        {data.resolution_note === null ? null : (
          <div className="cp-requisites__wide">
            <dt>Заметка о разборе</dt>
            <dd>{data.resolution_note}</dd>
          </div>
        )}
      </dl>

      {context.length === 0 ? null : (
        <section className="cp-section" aria-labelledby="mb-context-title">
          <h3 id="mb-context-title">Безопасный контекст</h3>
          <dl className="cp-requisites">
            {context.map(([key, value]) => (
              <div key={key}>
                <dt>{key}</dt>
                <dd>
                  <code>{contextValue(value)}</code>
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      <section className="cp-section" aria-labelledby="mb-failure-actions">
        <h3 id="mb-failure-actions">Что можно сделать</h3>
        <div className="mb-actions">
          {canRetry ? (
            <button
              type="button"
              className="button button--primary button--compact"
              disabled={retry.isPending}
              onClick={() => retry.mutate()}
            >
              {retry.isPending ? "Ставим в очередь…" : "Повторить"}
            </button>
          ) : null}
          {canResolve && !resolving ? (
            <button
              type="button"
              className="button button--secondary button--compact"
              onClick={() => setResolving(true)}
            >
              Отметить разобранной
            </button>
          ) : null}
        </div>
        {!canRetry ? (
          <p className="muted" data-testid="retry-unavailable">
            {data.status === "resolved"
              ? "Ошибка уже разобрана, повтор не нужен."
              : "Повтор недоступен: Core пометил ошибку как неповторяемую. Обычно так бывает, когда ящик отклонил пароль — обновите доступ к ящику и запустите синхронизацию."}
          </p>
        ) : null}
        {retry.isError ? (
          <MailboxNotice
            error={retry.error}
            fallback="Не удалось повторить."
            onRetry={() => retry.mutate()}
            retrying={retry.isPending}
          />
        ) : null}
        {retry.isSuccess ? (
          <p className="mb-result mb-result--ok" role="status">
            Повтор поставлен в очередь. Состояние ошибки обновится после
            обработки письма.
          </p>
        ) : null}
        {resolving && canResolve ? (
          <ResolveForm
            pending={resolve.isPending}
            error={resolve.error}
            onChange={() => resolveKey.reset()}
            onCancel={() => {
              resolve.reset();
              setResolving(false);
            }}
            onSubmit={(input) => resolve.mutate(input)}
          />
        ) : null}
        {resolve.isSuccess ? (
          <p className="mb-result mb-result--ok" role="status">
            Ошибка отмечена разобранной.
          </p>
        ) : null}
      </section>
    </article>
  );
}
